import { AppDispatch, RootState } from "../app/store";
import { OnboardingType } from "../types";
import {
  onUpdateActiveTemplate,
  onUpdateOnboardingType,
} from "../reducers/activeTemplateReducer";
import { FormikValues } from "formik";
import {
  onUpdateStep,
  onUpdateSubStep,
} from "../reducers/onboardingDataReducer";
import {
  onUpdateStep as onUpdateSecuritiesStep,
  onUpdateSubStep as onUpdateSecuritiesSubStep,
  onUpdateHolder,
} from "../reducers/securitiesReducer";
import onboardingSecurities from "../templates/allaria/securities";
import onboardingSignatory from "../templates/allaria/signatory";
import onboardingJuridical from "../templates/allaria/juridical";
import onboardingPhysical from "../templates/allaria/physical";
import { useEffect } from "react";
import onboardingInteractiveBrokers from "../templates/allaria/interactiveBrokers";

const useTemplateInterpreter = ({
  dispatch,
  onboardingType,
  state,
  values,
  draftUUID: uuid,
}: {
  dispatch: AppDispatch;
  onboardingType: OnboardingType;
  state: RootState;
  values?: FormikValues;
  draftUUID?: string;
}) => {
  const {
    onboardingData: {
      stepData: { step, subStep },
    },
  } = state;

  const { draftUUID, formValues } =
    onboardingType === "SECURITIES" ? state.securities : state.onboardingData;

  const loadTemplate = async () => {
    if (!onboardingType) {
      window.location.href = "/";
      return;
    }

    if (onboardingType === "PHYSICAL") {
      const template = await onboardingPhysical(
        uuid || draftUUID,
        dispatch,
        formValues
      );
      dispatch(onUpdateActiveTemplate(template));
    }

    if (onboardingType === "JURIDICAL") {
      const template = await onboardingJuridical(uuid || draftUUID, dispatch);
      dispatch(onUpdateActiveTemplate(template));
    }

    if (onboardingType === "SIGNATORY") {
      const template = await onboardingSignatory(dispatch);
      dispatch(onUpdateActiveTemplate(template));
    }

    if (onboardingType === "SECURITIES") {
      const template = await onboardingSecurities({
        values: values ?? (formValues || {}),
        state,
        dispatch,
      });
      dispatch(onUpdateActiveTemplate(template));
    }

    if (onboardingType === "INTERACTIVE_BROKERS") {
      const template = await onboardingInteractiveBrokers({
        values: values ?? (formValues || {}),
        state,
        dispatch,
      });
      dispatch(onUpdateActiveTemplate(template));
    }
  };

  const redirectToOnboarding = () => {
    if (window.location.pathname === "/personal") {
      dispatch(onUpdateOnboardingType("PHYSICAL"));
      dispatch(onUpdateStep(step || 1));
      dispatch(onUpdateSubStep(subStep || 1));
    }

    if (window.location.pathname === "/empresarial") {
      dispatch(onUpdateOnboardingType("JURIDICAL"));
      dispatch(onUpdateStep(1));
      dispatch(onUpdateSubStep(1));
    }

    if (window.location.pathname === "/firmante") {
      dispatch(onUpdateOnboardingType("SIGNATORY"));
      dispatch(onUpdateStep(1));
      dispatch(onUpdateSubStep(1));
    }

    if (window.location.pathname === "/securities") {
      dispatch(onUpdateOnboardingType("SECURITIES"));
      dispatch(onUpdateHolder(1));
      dispatch(onUpdateSecuritiesStep("0"));
      dispatch(onUpdateSecuritiesSubStep(1));
    }

    if (window.location.pathname === "/interactive-brokers") {
      dispatch(onUpdateOnboardingType("INTERACTIVE_BROKERS"));
      dispatch(onUpdateStep(1));
      dispatch(onUpdateSubStep(1));
    }
  };

  // To redirect in preconfirmation "handleBack"
  const getUrlTemplate = () => {
    switch (onboardingType) {
      case "PHYSICAL":
        return "/personal";
      case "JURIDICAL":
        return "/empresarial";
      case "SIGNATORY":
        return `/firmante?externalCode=${uuid || draftUUID}&email=${
          formValues?.email
        }`;
      case "SECURITIES":
        return "/securities";
      case "INTERACTIVE_BROKERS":
        return "/interactive-brokers";
      default:
        return "/";
    }
  };

  const urlTemplate = getUrlTemplate();

  const language =
    (onboardingType === "SECURITIES"
      ? state.securities.language
      : state.onboardingData.language) ?? "es";

  const shouldCheckForDraft = onboardingType === "SECURITIES"

  // Reload interactive brokers template when its data changes
  useEffect(() => {
    if (onboardingType === "INTERACTIVE_BROKERS") {
      loadTemplate();
    }
  }, [state.interactiveBrokers, onboardingType]);

  return {
    formValues,
    loadTemplate,
    urlTemplate,
    draftUUID,
    redirectToOnboarding,
    language,
    shouldCheckForDraft
  };
};

export default useTemplateInterpreter;
